import {
  TdsHeader,
  TdsHeaderBrandSymbol,
  TdsHeaderDropdown,
  TdsHeaderDropdownList,
  TdsHeaderDropdownListItem,
  TdsHeaderDropdownListUser,
  TdsHeaderHamburger,
  TdsHeaderLauncher,
  TdsHeaderLauncherList,
  TdsHeaderLauncherListItem,
  TdsHeaderTitle
} from '@scania/tegel-react'
import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'

import { getClient } from '@cdab/scania/qpr/contexts/backend-provider'
import { useFeatures } from '@cdab/scania/qpr/feature-flags'
import { useCssVariableBreakpoint } from '@cdab/scania/qpr/hooks'
import { useTranslation } from 'react-i18next'

export interface ToolbarHeaderProps {
  onMobileMenuClick: () => void
  title: string
  mobileTitle: string
  currentActiveMenuItem?: number
  mobileDescription: string | null
  user: {
    userName: string
    name: string
  }
}

function useAppLinks(): JSX.Element[] {
  const features = useFeatures([
    'release_notes',
    'show_qpr_quick_guide',
    'show_qpr_launch_webinar',
    'show_qpr_launch_webinar_2',
    'show_qpr_news_standards_commercial'
  ])
  const { t } = useTranslation()

  const appLinks = []

  if (features.release_notes.enabled) {
    appLinks.push(
      <TdsHeaderLauncherListItem key='release-notes'>
        <a href={features.release_notes.value.release_notes_url}>
          {t('release-notes')}
        </a>
      </TdsHeaderLauncherListItem>
    )

    appLinks.push(
      <TdsHeaderLauncherListItem key='known-bugs'>
        <a href={features.release_notes.value.known_bugs_url}>
          {t('known-bugs')}
        </a>
      </TdsHeaderLauncherListItem>
    )
  }

  if (features.show_qpr_launch_webinar.enabled) {
    appLinks.push(
      <TdsHeaderLauncherListItem key='qpr-launch-webinar'>
        <a href={features.show_qpr_launch_webinar.value.url}>
          {features.show_qpr_launch_webinar.value.text}
        </a>
      </TdsHeaderLauncherListItem>
    )
  }

  if (features.show_qpr_launch_webinar_2.enabled) {
    appLinks.push(
      <TdsHeaderLauncherListItem key='qpr-launch-webinar-2'>
        <a href={features.show_qpr_launch_webinar_2.value.url}>
          {features.show_qpr_launch_webinar_2.value.text}
        </a>
      </TdsHeaderLauncherListItem>
    )
  }

  if (features.show_qpr_quick_guide.enabled) {
    appLinks.push(
      <TdsHeaderLauncherListItem key='qpr-quick-guide'>
        <a href={features.show_qpr_quick_guide.value.url}>
          {features.show_qpr_quick_guide.value.text}
        </a>
      </TdsHeaderLauncherListItem>
    )
  }

  if (features.show_qpr_news_standards_commercial.enabled) {
    appLinks.push(
      <TdsHeaderLauncherListItem key='show_qpr_news_standards_commercial'>
        <a href={features.show_qpr_news_standards_commercial.value.url}>
          {features.show_qpr_news_standards_commercial.value.text}
        </a>
      </TdsHeaderLauncherListItem>
    )
  }

  return appLinks
}

export function ToolbarHeader(props: ToolbarHeaderProps) {
  const { user, title, onMobileMenuClick, mobileTitle } = props
  const isLg = useCssVariableBreakpoint('--tds-grid-lg')
  const appLinks = useAppLinks()
  const navigate = useNavigate()

  const onLogoClick = useCallback(() => {
    navigate('/')
  }, [navigate])

  const onLogoutClick = () => {
    const client = getClient()
    client.Logout({ redirectUri: `${window.location.origin}/loggedout` })
  }

  const displayTitle = isLg ? title : mobileTitle

  return (
    <div className='tds-header-wrapper'>
      <TdsHeader>
        <TdsHeaderHamburger
          onClick={onMobileMenuClick}
          aria-label='Open application drawer'
          aria-haspopup='true'
          aria-expanded='false'
        />

        <TdsHeaderTitle>{displayTitle}</TdsHeaderTitle>

        <TdsHeaderDropdown slot='end' noDropdownIcon aria-label='User menu'>
          <img
            slot='icon'
            src='https://www.svgrepo.com/show/384676/account-avatar-profile-user-6.svg'
            alt='User menu'
          />
          <TdsHeaderDropdownList>
            <TdsHeaderDropdownListUser
              header={user.name}
              subheader={user.userName}
            />
            <TdsHeaderDropdownListItem>
              <a href='/profile'>My Profile</a>
            </TdsHeaderDropdownListItem>
            <TdsHeaderDropdownListItem onClick={onLogoutClick}>
              <a>Log out</a>
            </TdsHeaderDropdownListItem>
          </TdsHeaderDropdownList>
        </TdsHeaderDropdown>

        <TdsHeaderLauncher slot='end' aria-label='App launcher menu'>
          <TdsHeaderLauncherList>
            {appLinks.map(link => link)}
          </TdsHeaderLauncherList>
        </TdsHeaderLauncher>

        <TdsHeaderBrandSymbol slot='end'>
          <a href='/' onClick={onLogoClick} aria-label='Home'></a>
        </TdsHeaderBrandSymbol>
      </TdsHeader>
    </div>
  )
}
